import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { ProjectInput, projectStatus } from "@reina/shared";
import type { Project } from "@reina/shared";
import { Button, Field, FieldGrid, Modal, useToast } from "../../components";
import { useCreateProject, useUpdateProject } from "../../api/pricings";
import { errorMessage } from "../../lib/format";
import { textOrNull } from "../../lib/form";

/** Create/edit project modal, shared by the list and detail screens. */
export function ProjectModal({ project, onClose }: { project: Project | null; onClose: () => void }) {
  const toast = useToast();
  const create = useCreateProject();
  const update = useUpdateProject();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof ProjectInput>, unknown, z.output<typeof ProjectInput>>({
    resolver: zodResolver(ProjectInput),
    defaultValues: {
      name: project?.name ?? "",
      clientDeveloper: project?.clientDeveloper ?? "",
      description: project?.description ?? "",
      status: project?.status ?? "active",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (project) {
        await update.mutateAsync({ id: project.id, input: values });
        toast.push({ message: "Project updated", tone: "ok" });
      } else {
        await create.mutateAsync(values);
        toast.push({ message: "Project created", tone: "ok" });
      }
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title={project ? "Edit project" : "New project"}
      sub={project ? project.name : "a development / site to price"}
      onClose={onClose}
      footer={
        <>
          <span className="sub">
            {project ? "Changes are stamped and audited." : "Pricings are added from the project page."}
          </span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="project-form" disabled={isSubmitting}>
              {project ? "Save changes" : "Create project"}
            </Button>
          </div>
        </>
      }
    >
      <form id="project-form" className="stack" onSubmit={onSubmit} noValidate>
        <FieldGrid>
          <Field label="Name" error={errors.name?.message}>
            <input className="field" autoFocus {...register("name")} />
          </Field>
          <Field
            label="Client / developer"
            hint="e.g. Pentland Homes"
            error={errors.clientDeveloper?.message}
          >
            <input className="field" {...register("clientDeveloper", { setValueAs: textOrNull })} />
          </Field>
        </FieldGrid>
        <Field label="Description" error={errors.description?.message}>
          <textarea
            className="field"
            rows={3}
            {...register("description", { setValueAs: textOrNull })}
          />
        </Field>
        {project && (
          <Field label="Status" hint="Archived projects are hidden from day-to-day lists.">
            <select className="field" {...register("status")}>
              {projectStatus.options.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
        )}
      </form>
    </Modal>
  );
}
